import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';

// Fetch labels for a specific card
export const fetchCardLabels = createAsyncThunk(
    'labels/fetchCardLabels',
    async (cardId, thunkAPI) => {
        try {
            const response = await fetch(config.API_URI + `/v1/cards/${cardId}/labels`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch labels');
            }

            const data = await response.json();
            return { cardId, labels: data.labels };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// Fetch all labels for a board
export const fetchLabels = createAsyncThunk(
    'labels/fetchLabels',
    async ({ boardId, search = '' }, thunkAPI) => {
        try {
            
            const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
            const response = await fetch(config.API_URI + `/v1/boards/${boardId}/labels${searchParam}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch labels');
            }

            const data = await response.json();
            return { boardId, labels: data.labels, search };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// Optimistic update to add/remove labels
export const updateLabelOnCard = createAsyncThunk(
    'labels/updateLabelOnCard',
    async ({ cardId, labelId, boardId, isChecked }, { dispatch, getState, rejectWithValue }) => {
        try {
            
            const method = isChecked ? 'PUT' : 'DELETE';
            const response = await fetch(`${config.API_URI}/v1/cards/${cardId}/labels/${labelId}`, {
                method,
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                body: JSON.stringify({ boardId }),
            });
            if (!response.ok) throw new Error(`Failed to ${isChecked ? 'add' : 'remove'} label`);
            return { labelId, isChecked };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const labelSlice = createSlice({
    name: 'labels',
    initialState: {
        labelsByCard: {},   // Store labels per card
        labelsByBoard: {},  // Store labels per board
        status: 'idle',
        error: null
    },
    reducers: {
        addLabelOptimistically: (state, action) => {
            const { boardId, newLabel } = action.payload;
            if (!state.labelsByBoard[boardId]) {
                state.labelsByBoard[boardId] = [];
            }
            state.labelsByBoard[boardId].push(newLabel);
        },
        updateLabelOptimistically: (state, action) => {
            const { boardId, updatedLabel } = action.payload;
            const index = state.labelsByBoard[boardId]?.findIndex(label => label._id === updatedLabel._id);
            if (index !== undefined && index >= 0) {
                state.labelsByBoard[boardId][index] = updatedLabel;
            }
        },
        removeLabelOptimistically: (state, action) => {
            const { boardId, labelId } = action.payload;
            const index = state.labelsByBoard[boardId]?.findIndex(label => label._id === labelId);
            if (index !== undefined && index >= 0) {
                state.labelsByBoard[boardId].splice(index, 1);
            }
        },
        toggleLabelOnCardOptimistically: (state, action) => {
            const { cardId, labelId, isChecked } = action.payload;
            const cardLabels = state.labelsByCard[cardId] || [];

            // Find the label object in the board's label list
            const label = Object.values(state.labelsByBoard)
                .flat()
                .find(l => l._id === labelId);

            if (!label) return; // If label is not found, exit early

            // Optimistically add or remove label
            if (isChecked) {
                state.labelsByCard[cardId] = [...cardLabels, label];
            } else {
                state.labelsByCard[cardId] = cardLabels.filter(l => l._id !== labelId);
            }
        },

        // ========== REAL-TIME OPTIMISTIC ACTIONS ==========

        // Add label to card from real-time event
        optimisticAddLabelToCard: (state, action) => {
            const { cardId, label } = action.payload;
            const cardLabels = state.labelsByCard[cardId] || [];

            // Check if label already exists (avoid duplicates)
            const exists = cardLabels.some(l => l._id === label._id);
            if (!exists) {
                state.labelsByCard[cardId] = [...cardLabels, label];
            }
        },

        // Remove label from card from real-time event
        optimisticRemoveLabelFromCard: (state, action) => {
            const { cardId, labelId } = action.payload;
            const cardLabels = state.labelsByCard[cardId] || [];
            state.labelsByCard[cardId] = cardLabels.filter(l => l._id !== labelId);
        },

        // Update label from real-time event
        optimisticUpdateLabel: (state, action) => {
            const { boardId, label } = action.payload;

            // Update in board labels
            if (state.labelsByBoard[boardId]) {
                const index = state.labelsByBoard[boardId].findIndex(l => l._id === label._id);
                if (index !== -1) {
                    state.labelsByBoard[boardId][index] = label;
                }
            }

            // Update in all card labels that use this label
            Object.keys(state.labelsByCard).forEach(cardId => {
                const cardLabels = state.labelsByCard[cardId];
                const labelIndex = cardLabels.findIndex(l => l._id === label._id);
                if (labelIndex !== -1) {
                    state.labelsByCard[cardId][labelIndex] = label;
                }
            });
        },

        // Delete label from real-time event
        optimisticDeleteLabel: (state, action) => {
            const { boardId, labelId } = action.payload;

            // Remove from board labels
            if (state.labelsByBoard[boardId]) {
                state.labelsByBoard[boardId] = state.labelsByBoard[boardId].filter(l => l._id !== labelId);
            }

            // Remove from all card labels
            Object.keys(state.labelsByCard).forEach(cardId => {
                state.labelsByCard[cardId] = state.labelsByCard[cardId].filter(l => l._id !== labelId);
            });
        },

        // Set card labels from real-time event (replaces entire list)
        setCardLabelsFromSocket: (state, action) => {
            const { cardId, labels } = action.payload;
            state.labelsByCard[cardId] = labels;
        },

    },
    extraReducers: (builder) => {
        builder
            // Handle board-wide labels fetching
            .addCase(fetchLabels.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchLabels.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const { boardId, labels } = action.payload;
                state.labelsByBoard[boardId] = labels;  // Store board labels by board ID
            })
            .addCase(fetchLabels.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            // Handle card-specific labels fetching
            .addCase(fetchCardLabels.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchCardLabels.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const { cardId, labels } = action.payload;
                state.labelsByCard[cardId] = labels;  // Store card labels by card ID
            })
            .addCase(fetchCardLabels.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(updateLabelOnCard.pending, (state, action) => {
                const { cardId, labelId, isChecked } = action.meta.arg;
                const cardLabels = state.labelsByCard[cardId] || [];

                // Find the label object in the board's label list
                const label = Object.values(state.labelsByBoard)
                    .flat()
                    .find(l => l._id === labelId);

                if (!label) return; // If label is not found, exit early

                // Optimistically toggle the label state
                if (isChecked) {
                    state.labelsByCard[cardId] = [...cardLabels, label];
                } else {
                    state.labelsByCard[cardId] = cardLabels.filter(l => l._id !== labelId);
                }
            })
            .addCase(updateLabelOnCard.rejected, (state, action) => {
                const { cardId, labelId, isChecked } = action.meta.arg;
                const cardLabels = state.labelsByCard[cardId] || [];

                // Find the label object in the board's label list
                const label = Object.values(state.labelsByBoard)
                    .flat()
                    .find(l => l._id === labelId);

                if (!label) return; // If label is not found, exit early

                // Rollback based on the isChecked state
                if (isChecked) {
                    state.labelsByCard[cardId] = cardLabels.filter(l => l._id !== labelId);
                } else {
                    state.labelsByCard[cardId] = [...cardLabels, label];
                }

                state.error = action.payload;
            })

    }
});

export const {
    addLabelOptimistically,
    updateLabelOptimistically,
    removeLabelOptimistically,
    toggleLabelOnCardOptimistically,
    // Real-time optimistic actions
    optimisticAddLabelToCard,
    optimisticRemoveLabelFromCard,
    optimisticUpdateLabel,
    optimisticDeleteLabel,
    setCardLabelsFromSocket,
} = labelSlice.actions;

export default labelSlice.reducer;
